import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

export interface LogRelayConfig {
  endpoint: string;
  batchSize?: number;
  batchDelay?: number;
}

@Injectable({ providedIn: 'root' })
export class LogRelayService {
  private readonly endpoint: string;
  private readonly batchSize: number;
  private readonly batchDelay: number;

  private batch: any[] = [];
  private batchTimeout: any;

  constructor(private http: HttpClient) {
    // Configurable: pull from env or global config or DI token if desired
    const config: LogRelayConfig = (window as any).logRelayConfig || {};
    this.endpoint = config.endpoint || 'https://YOUR_API_GATEWAY_DOMAIN/api/log-error';
    this.batchSize = config.batchSize ?? 20;
    this.batchDelay = config.batchDelay ?? 2000;

    // On load, flush any dropped logs from previous session
    this.retryDroppedLogs();

    // Flush batch before navigation/close
    window.addEventListener('beforeunload', () => {
      this.flushBatch(true);
    });
  }

  logToCloud(log: any, isCritical = false) {
    if (isCritical) {
      // Instant send for errors/warnings (no batching)
      this.sendLogs([log], false);
    } else {
      this.logBatch(log);
    }
  }

  logBatch(log: any) {
    this.batch.push(log);
    if (this.batch.length >= this.batchSize) {
      this.flushBatch();
    } else if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => this.flushBatch(), this.batchDelay);
    }
  }

  flushBatch(useBeacon = false) {
    if (this.batch.length === 0) return;
    const logsToSend = [...this.batch];
    this.batch = [];
    clearTimeout(this.batchTimeout);
    this.batchTimeout = null;
    this.sendLogs(logsToSend, useBeacon);
  }

  private sendLogs(logs: any[], useBeacon: boolean) {
    let sent = false;

    if (useBeacon && navigator.sendBeacon) {
      try {
        sent = navigator.sendBeacon(this.endpoint, JSON.stringify(logs));
      } catch {
        sent = false;
      }
    }

    if (!sent) {
      this.http.post(this.endpoint, logs).subscribe({
        error: () => this.logDroppedLogs(logs)
      });
    }
  }

  private logDroppedLogs(logs: any[]) {
    const dropped = JSON.parse(localStorage.getItem('droppedLogs') || '[]');
    dropped.push(...logs);
    localStorage.setItem('droppedLogs', JSON.stringify(dropped));
  }

  private retryDroppedLogs() {
    const dropped = JSON.parse(localStorage.getItem('droppedLogs') || '[]');
    if (dropped.length) {
      this.http.post(this.endpoint, dropped).subscribe({
        next: () => localStorage.removeItem('droppedLogs'),
        error: () => {} // Retain on failure
      });
    }
  }
}
